import React from 'react';
import { GetCatalogProductDetailsQuery, ProductCatalogStorefrontStatus, ProductCatalogItemTilePresentationType, ProductUniversalItemSource } from '@graphql/generated';
import { CatalogItemTile, ExternalProduct } from '../../../../Catalog.types';
import { SkeletonThumbnail, SkeletonText } from '@shared/components/Skeleton';
import { Box, BoxProps, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { ProductTile } from '../../../ProductTile';

export type OnProductClickArgs = {
  itemPosition: number;
  product: ExternalProduct;
  productSource?: ProductUniversalItemSource;
};

export type OnQuickAddClickArgs = OnProductClickArgs & {
  quantity: number;
};

export type ExternalProductTileProps = {
  index: number;
  product: ExternalProduct;
  isMobile?: boolean;
  keyPrefix?: string;
  productSource?: ProductUniversalItemSource;
  onProductClick?: (args: OnProductClickArgs) => void;
  onQuickAddClick?: (args: OnQuickAddClickArgs) => void;
};

const externalProductToCatalogItem = (product: ExternalProduct): GetCatalogProductDetailsQuery['productCatalogItemById'] & CatalogItemTile => {
  const photos = product.photos.map(photo => ({ url: photo.url, id: photo.url }));
  const listPriceRange = {
    isSinglePrice: true,
    lowerPrice: product.listPrice,
    upperPrice: product.listPrice
  };
  const salePriceRange = product.salePrice
    ? {
        isSinglePrice: true,
        lowerPrice: product.salePrice,
        upperPrice: product.salePrice
      }
    : undefined;
  const rating = product.rating
    ? {
        id: product.rating.id,
        stats: {
          id: product.rating.id,
          averageRating: product.rating.averageRating,
          totalReviewCount: product.rating.reviewsCount
        }
      }
    : undefined;
  const isFullPrice = !Boolean(product.salePrice);

  return {
    id: product.id,
    title: product.title,
    itemType: 'gift',
    mediaAssets: [],
    storefrontInfo: {
      canAddToCart: true,
      canAddToRegistry: true,
      storefrontStatus: ProductCatalogStorefrontStatus.available
    },
    brand: {
      id: product.storeName,
      name: product.storeName,
      publicUrlPath: (() => {
        try {
          return new URL(product.url).origin;
        } catch {
          return location.origin;
        }
      })()
    },
    rating,
    price: product.salePrice ?? product.listPrice,
    fullPrice: product.listPrice,
    isFullPrice,
    parentCategories: [],
    subCategories: [],
    photos,
    collectionDisplayInfos: [],
    groupDimensionOptions: [],
    tilePresentation: {
      photos,
      presentationType: ProductCatalogItemTilePresentationType.parentDetails,
      title: product.title,
      shortDescription: product.description,
      fullPriceRange: listPriceRange,
      priceRange: salePriceRange ?? listPriceRange,
      isFullPrice
    }
  };
};

export const ExternalProductTile = ({ index, keyPrefix, isMobile, onQuickAddClick, onProductClick, product, productSource: source }: ExternalProductTileProps) => {
  const item = externalProductToCatalogItem(product);
  const productSource = source ?? ProductUniversalItemSource.googleShoppingRegular;
  return (
    <ProductTile
      index={index}
      key={keyPrefix ?? '' + item.id}
      addItemLoading={false}
      product={item}
      deriveTelemetryPageForProduct={() => 'searchResults'}
      addCatalogItem={args => {
        onQuickAddClick?.({ itemPosition: index, product, productSource, quantity: args.payload.qty });
      }}
      onQuickAddClick={event => {
        event.stopPropagation();
        onQuickAddClick?.({ itemPosition: index, product, productSource, quantity: 1 });
      }}
      onDetailClick={event => {
        event.stopPropagation();
        onProductClick?.({ itemPosition: index, product, productSource });
      }}
      categoryType={'category'}
      isMobile={isMobile}
      hasAnyStorefrontWarning={false}
      componentSource={'productGrid'}
      variant={'gridItem'}
      shouldOpenPdp={false}
      readyProduct={item}
    />
  );
};

const ProductTileMetadata: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Flex flexDirection="column" rowGap={1} marginTop={4} {...rest}>
      {children}
    </Flex>
  );
};

ExternalProductTile.Skeleton = () => {
  return (
    <Box>
      <SkeletonThumbnail width={'100%'} height={'unset'} paddingTop="100%" />
      <ProductTileMetadata marginTop={5} rowGap={3}>
        <SkeletonText rows={2} spacing={3} skeletonHeight={[pxToRem(16), null, null, pxToRem(18)]} />
        <SkeletonText rows={1} skeletonHeight={pxToRem(14)} width={9} />
        <SkeletonText rows={1} skeletonHeight={pxToRem(14)} width={pxToRem(80)} />
      </ProductTileMetadata>
    </Box>
  );
};
